/**
 * Erweiterung: neue Farben/Größen an BESTEHENDE Artikel hängen (16.10.2026).
 *
 * Matze: „Sollten die Farbenerweiterungen nicht in dieses Tool mit reinkommen? … nicht
 * unter Neuanlage, sondern unter Bestandsupdate." Ein Schritt im Pfad „Bestand erweitern /
 * aktualisieren": Plan laden (liest den Ziel-Artikel live, schreibt nichts) → je Artikel
 * Ziel und neue Varianten prüfen → Tippbestätigung ANHAENGEN. Die neuen Varianten sind
 * direkt aktiv, Preise wie bei der Neuanlage. Pfade OHNE `/api/`-Prefix.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { GitBranchPlus, ListChecks, Undo2 } from 'lucide-react';
import { api } from '@libero/core/services/api';
import LaufButton from './LaufButton';
import Info from './Info';
import Schritt from './Schritt';
import { zahl } from '../lib/format';
import type { LaufStand } from '../lib/typen';

interface Variante { index: number; nummer: string; farbe: string; groesse: string; ean: string; preise: Record<string, number | null> }
interface ErwArtikel {
  artikelSchluessel: string;
  name: string;
  kandidaten: number[];
  ziel: { itemId: number | null; grund: string };
  anhaengen: Variante[];
  wartetAufWert: Array<Variante & { fehlend: string[] }>;
  uebersprungen: Array<Variante & { grund: string }>;
  blockiert: string | null;
}
interface Plan {
  artikel: ErwArtikel[];
  anhaengen: number;
  /** Varianten, deren neue Farbe/Größe der Lauf vorher in Plenty anlegt. */
  wartetAufWert: number;
  neueWerte: Array<{ attributId: number; wert: string }>;
  uebersprungen: number;
  ohneZiel: number;
}

const PFAD = '/programmes/artikelimport';
const euro = (w: number | null | undefined) => (w == null ? '–' : `${w.toFixed(2).replace('.', ',')} €`);

export default function ErweiterungPanel({ entwurfId, schritt }: { entwurfId: number; schritt: number }) {
  const [plan, setPlan] = useState<Plan | null>(null);
  const [laeuft, setLaeuft] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [bestaetigung, setBestaetigung] = useState('');
  const [stand, setStand] = useState<LaufStand | null>(null);
  const [zuruecknahme, setZuruecknahme] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => () => { if (timer.current) clearInterval(timer.current); }, []);

  const laden = useCallback(async () => {
    setLaeuft('Plan');
    setFehler(null);
    try {
      const r = await api.get<{ data: Plan }>(`${PFAD}/entwuerfe/${entwurfId}/erweiterung`);
      setPlan(r.data.data);
    } catch (e: any) {
      setFehler(e?.response?.data?.error?.message ?? 'Der Plan ließ sich nicht laden.');
    } finally {
      setLaeuft(null);
    }
  }, [entwurfId]);

  const zielSetzen = async (artikelSchluessel: string, wert: string) => {
    setFehler(null);
    try {
      await api.put(`${PFAD}/entwuerfe/${entwurfId}/erweiterung/ziel`, { artikelSchluessel, itemId: wert === '' ? null : Number(wert) });
      await laden();
    } catch (e: any) {
      setFehler(e?.response?.data?.error?.message ?? 'Das Ziel ließ sich nicht speichern.');
    }
  };

  const standHolen = useCallback(async (id: number) => {
    try {
      const r = await api.get<{ data: LaufStand }>(`${PFAD}/laeufe/${id}`);
      setStand(r.data.data);
      if (r.data.data.status !== 'laufend' && timer.current) {
        clearInterval(timer.current);
        timer.current = null;
      }
    } catch {
      // Ein verlorener Poll ist kein Grund, die Anzeige zu leeren.
    }
  }, []);

  const anhaengen = async (nurArtikel?: string[]) => {
    setLaeuft(nurArtikel ? `Test ${nurArtikel[0]}` : 'Anhängen');
    setFehler(null);
    try {
      const r = await api.post<{ data: { laufId: number } }>(`${PFAD}/entwuerfe/${entwurfId}/erweiterung`, {
        bestaetigung: 'ANHAENGEN', ...(nurArtikel ? { nurArtikel } : {}),
      });
      setBestaetigung('');
      const id = r.data.data.laufId;
      await standHolen(id);
      if (timer.current) clearInterval(timer.current);
      timer.current = setInterval(() => void standHolen(id), 3000);
    } catch (e: any) {
      setFehler(e?.response?.data?.error?.message ?? 'Der Lauf ließ sich nicht starten.');
    } finally {
      setLaeuft(null);
    }
  };

  const zuruecknehmen = async () => {
    if (!stand) return;
    setLaeuft('Zurücknehmen');
    try {
      const r = await api.post<{ data: { bleibtInaktiv: number } }>(`${PFAD}/laeufe/${stand.id}/rollback`, { bestaetigung: 'DEAKTIVIEREN' });
      setZuruecknahme(`${zahl(r.data.data.bleibtInaktiv)} angehängte Varianten deaktiviert (nicht gelöscht).`);
    } catch (e: any) {
      setFehler(e?.response?.data?.error?.message ?? 'Zurücknehmen fehlgeschlagen.');
    } finally {
      setLaeuft(null);
    }
  };

  const z = (stand?.zusammenfassung ?? {}) as Record<string, any>;
  const laufFertig = stand != null && stand.status !== 'laufend';

  return (
    <Schritt
      nummer={schritt}
      titel="Neue Farben und Größen anhängen"
      ton="gefahr"
      zustand={stand
        ? (stand.status === 'laufend'
          ? `Lauf ${stand.id} hängt an …`
          : `Lauf ${stand.id}: ${zahl(z.variantenAngelegt ?? 0)} Varianten angehängt, ${zahl(z.variantenFehler ?? 0)} Fehler`)
        : plan
          ? `${zahl(plan.anhaengen + plan.wartetAufWert)} neue Varianten an ${zahl(plan.artikel.filter(a => a.anhaengen.length + a.wartetAufWert.length > 0).length)} Artikel · ${zahl(plan.ohneZiel)} ohne Ziel`
          : 'Plan laden — liest die Ziel-Artikel live, schreibt nichts.'}
      erledigt={laufFertig}
    >
      <div className="space-y-3 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <LaufButton
            variant="outline"
            size="sm"
            laeuft={laeuft === 'Plan'}
            laufText="Ziel-Artikel werden gelesen"
            icon={<ListChecks className="mr-2 h-4 w-4" />}
            onClick={() => void laden()}
            disabled={laeuft != null}
          >
            {plan ? 'Plan neu laden' : 'Plan laden'}
          </LaufButton>
          <Info titel="Was passiert hier?">
            <p>Bestehende Artikel bekommen ihre neuen Farben/Größen aus der Herstellerliste — als neue Varianten am selben Artikel, nicht als neuer Artikel.</p>
            <p>Ziel ist der Artikel, dessen Hauptvariante die Modellnummer trägt (z.&nbsp;B. „TIRO24 SWHOOD"). Gibt es mehrere Kandidaten (Teamshop-Kopien), wählst du hier.</p>
            <p>Preise wie bei der Neuanlage. Die neuen Varianten sind <strong>direkt aktiv</strong>. Kategorien, Märkte und Eigenschaften erben sie von der Hauptvariante. Bilder der neuen Farben werden mit hochgeladen.</p>
          </Info>
        </div>

        {plan && plan.artikel.length === 0 && (
          <p className="text-[#1A1A1A]/70">Keiner der ausgewählten Artikel hat neue Farben oder Größen.</p>
        )}

        {plan && plan.artikel.length > 0 && (
          <ul className="space-y-2">
            {plan.artikel.map(a => (
              <li key={a.artikelSchluessel} className="rounded border border-[#DAD9D9] bg-white p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-[#1A1A1A]">{a.name}</span>
                  <span className="text-xs text-[#1A1A1A]/60">
                    {a.ziel.itemId ? `→ Artikel ${a.ziel.itemId} (${a.ziel.grund})` : a.ziel.grund}
                  </span>
                  {a.kandidaten.length > 1 && (
                    <select
                      value={a.ziel.grund === 'von Hand gewählt' && a.ziel.itemId ? String(a.ziel.itemId) : ''}
                      onChange={e => void zielSetzen(a.artikelSchluessel, e.target.value)}
                      className="rounded border border-[#DAD9D9] px-2 py-0.5 text-xs"
                    >
                      <option value="">automatisch</option>
                      {a.kandidaten.map(id => <option key={id} value={id}>Artikel {id}</option>)}
                    </select>
                  )}
                  {a.anhaengen.length + a.wartetAufWert.length > 0 && !stand && (
                    <LaufButton
                      variant="outline"
                      size="sm"
                      laeuft={laeuft === `Test ${a.artikelSchluessel}`}
                      laufText="wird angehängt"
                      icon={<GitBranchPlus className="mr-1 h-3.5 w-3.5" />}
                      onClick={() => void anhaengen([a.artikelSchluessel])}
                      disabled={laeuft != null || bestaetigung !== 'ANHAENGEN'}
                    >
                      Nur diesen anhängen
                    </LaufButton>
                  )}
                </div>
                {a.blockiert && <p className="mt-1 text-xs text-red-800">{a.blockiert}</p>}
                {a.anhaengen.length > 0 && (
                  <details className="mt-1 text-xs">
                    <summary className="cursor-pointer text-green-900">{zahl(a.anhaengen.length)} neue Varianten</summary>
                    <table className="mt-1 w-full text-left">
                      <thead className="text-[#1A1A1A]/60"><tr><th>Nummer</th><th>Farbe</th><th>Größe</th><th>EAN</th><th>Webshop</th></tr></thead>
                      <tbody>
                        {a.anhaengen.map(v => (
                          <tr key={v.index}>
                            <td className="font-mono">{v.nummer}</td><td>{v.farbe}</td><td>{v.groesse}</td>
                            <td className="font-mono">{v.ean}</td><td>{euro(v.preise.Webshop ?? null)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </details>
                )}
                {a.wartetAufWert.length > 0 && (
                  <details className="mt-1 text-xs">
                    <summary className="cursor-pointer text-amber-800">
                      {zahl(a.wartetAufWert.length)} mit neuer Farbe/Größe — wird beim Anhängen in Plenty angelegt
                    </summary>
                    <ul className="mt-1 space-y-0.5">
                      {a.wartetAufWert.map(v => (
                        <li key={v.index}><span className="font-mono">{v.nummer}</span> {v.farbe} / {v.groesse} — neu: {v.fehlend.join(', ')}</li>
                      ))}
                    </ul>
                  </details>
                )}
                {a.uebersprungen.length > 0 && (
                  <details className="mt-1 text-xs">
                    <summary className="cursor-pointer text-amber-800">{zahl(a.uebersprungen.length)} nicht angehängt</summary>
                    <ul className="mt-1 space-y-0.5">
                      {a.uebersprungen.map(v => (
                        <li key={v.index}><span className="font-mono">{v.nummer}</span> {v.farbe} / {v.groesse}: {v.grund}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </li>
            ))}
          </ul>
        )}

        {plan && plan.neueWerte.length > 0 && !stand && (
          <p className="text-xs text-amber-800">
            Beim Anhängen werden zuerst {zahl(plan.neueWerte.length)} neue Farb-/Größenwerte in Plenty angelegt
            (protokolliert im Reiter Historie): {plan.neueWerte.slice(0, 8).map(w => w.wert).join(', ')}
            {plan.neueWerte.length > 8 ? ' …' : ''}
          </p>
        )}

        {plan && plan.anhaengen + plan.wartetAufWert > 0 && !stand && (
          <div className="flex flex-wrap items-end gap-3">
            <label>
              <span className="mb-1 block text-[#1A1A1A]/70">Zum Bestätigen <strong>ANHAENGEN</strong> tippen</span>
              <input
                type="text"
                value={bestaetigung}
                onChange={e => setBestaetigung(e.target.value)}
                placeholder="ANHAENGEN"
                className="rounded border border-[#DAD9D9] px-3 py-2 font-mono"
              />
            </label>
            <LaufButton
              laeuft={laeuft === 'Anhängen'}
              laufText="Varianten werden angehängt"
              dauerHinweis="eine Variante je Schreibvorgang, danach die Bilder"
              icon={<GitBranchPlus className="mr-2 h-4 w-4" />}
              onClick={() => void anhaengen()}
              disabled={bestaetigung !== 'ANHAENGEN' || laeuft != null}
              className="bg-[#E9FF57] text-[#1A1A1A] hover:bg-[#d4ea4d]"
            >
              {`${zahl(plan.anhaengen + plan.wartetAufWert)} Varianten anhängen`}
            </LaufButton>
          </div>
        )}

        {stand && (
          <div className="space-y-2 rounded border border-[#DAD9D9] bg-white p-3">
            <p className="text-[#1A1A1A]/80">
              Lauf {stand.id}: {stand.status === 'laufend' ? 'läuft …' : stand.status}
              {' · '}{zahl(z.variantenAngelegt ?? 0)} angehängt
              {(z.variantenFehler ?? 0) > 0 && <span className="text-red-800"> · {zahl(z.variantenFehler)} Fehler</span>}
              {z.bilder && <> · {zahl(z.bilder.hochgeladen ?? 0)} Bilder</>}
            </p>
            {Array.isArray(z.fehler) && z.fehler.length > 0 && (
              <ul className="text-xs text-red-800">{z.fehler.map((f: any, i: number) => <li key={i}>{f.artikel}: {f.meldung}</li>)}</ul>
            )}
            {laufFertig && (z.variantenAngelegt ?? 0) > 0 && !zuruecknahme && (
              <LaufButton
                variant="outline"
                size="sm"
                laeuft={laeuft === 'Zurücknehmen'}
                laufText="wird deaktiviert"
                icon={<Undo2 className="mr-1 h-3.5 w-3.5" />}
                onClick={() => void zuruecknehmen()}
                disabled={laeuft != null}
              >
                Angehängte Varianten deaktivieren
              </LaufButton>
            )}
            {zuruecknahme && <p className="text-xs text-green-800">{zuruecknahme}</p>}
            {laufFertig && (
              <button type="button" className="text-xs underline" onClick={() => { setStand(null); void laden(); }}>
                Plan neu laden
              </button>
            )}
          </div>
        )}

        {fehler && <p className="text-xs text-red-800">{fehler}</p>}
      </div>
    </Schritt>
  );
}
